import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PendingReviews } from "./PendingReviews";
import { BeforeAfterSubmissions } from "./BeforeAfterSubmissions";
import { Loader2, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useProducts } from "@/hooks/useProducts";
import { Stars } from "@/components/site/ProductReviews";

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { product_slug: "", name: "", rating: 5, text: "", received_at: today(), order_reference: "", consent: false };
const field = "h-11 w-full rounded-sm border border-[var(--line)] bg-transparent px-3 text-sm outline-none focus:border-[var(--gold)]";
const label = "mb-1 block text-xs text-[var(--parchment)]/80";

function NewReview({ products, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/reviews", { ...f, rating: Number(f.rating) });
      toast.success("Avis publié sur la fiche produit");
      setF(EMPTY);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} data-testid="review-form" className="grid gap-4 rounded-sm border border-[var(--line)] p-6 md:grid-cols-2">
      <label><span className={label}>Produit</span>
        <select required value={f.product_slug} onChange={set("product_slug")} data-testid="review-product" className={`${field} bg-[var(--ink)]`}>
          <option value="">Choisir…</option>
          {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
        </select></label>
      <label><span className={label}>Prénom + initiale (ex. Awa D.)</span>
        <input required maxLength={60} value={f.name} onChange={set("name")} data-testid="review-name" className={field} /></label>
      <label><span className={label}>Note</span>
        <select value={f.rating} onChange={set("rating")} data-testid="review-rating" className={`${field} bg-[var(--ink)]`}>
          {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5</option>)}
        </select></label>
      <label><span className={label}>Date de réception de l'avis</span>
        <input type="date" required value={f.received_at} onChange={set("received_at")} data-testid="review-date" className={field} /></label>
      <label className="md:col-span-2"><span className={label}>Avis (copiez le texte de la cliente, sans le modifier)</span>
        <textarea required minLength={3} maxLength={2000} rows={4} value={f.text} onChange={set("text")} data-testid="review-text" className={`${field} h-auto py-2`} /></label>
      <label><span className={label}>N° de commande (facultatif, pour le badge « Achat vérifié »)</span>
        <input value={f.order_reference} onChange={set("order_reference")} data-testid="review-order" className={field} /></label>
      <label className="flex items-start gap-3 self-end text-sm">
        <input type="checkbox" required checked={f.consent} onChange={set("consent")} data-testid="review-consent" className="mt-1 h-4 w-4 accent-[var(--gold)]" />
        La cliente a donné son accord écrit pour publier son avis.
      </label>
      <button disabled={busy} data-testid="review-submit" className="flex h-11 items-center justify-center gap-2 rounded-full bg-[var(--gold)] px-6 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--ink)] disabled:opacity-50 md:w-fit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Publier l'avis
      </button>
    </form>
  );
}

function ReviewRow({ r, productName, onChanged }) {
  const toggle = async () => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { ...r, consent: true, active: !r.active });
      onChanged();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async () => {
    if (!window.confirm("Supprimer cet avis ?")) return;
    await api.delete(`/admin/reviews/${r.id}`);
    onChanged();
  };
  return (
    <li data-testid={`admin-review-${r.id}`} className="flex flex-wrap items-start justify-between gap-4 border-b border-[var(--line)] py-4">
      <div className="max-w-2xl text-sm">
        <p className="flex flex-wrap items-center gap-3"><Stars n={r.rating} /> <b>{r.name}</b> · {productName} · {r.received_at}{r.verified ? " · Achat vérifié" : ""}</p>
        <p className="mt-2 text-[var(--parchment)]/85">« {r.text} »</p>
      </div>
      <div className="flex gap-2">
        <button onClick={toggle} data-testid={`admin-review-toggle-${r.id}`} title={r.active ? "Masquer" : "Afficher"} className="rounded-full border border-[var(--line)] p-2 hover:border-[var(--gold)]">{r.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
        <button onClick={remove} data-testid={`admin-review-delete-${r.id}`} title="Supprimer" className="rounded-full border border-[var(--line)] p-2 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
      </div>
    </li>
  );
}

export function ReviewsAdmin() {
  const qc = useQueryClient();
  const { data: products } = useProducts();
  const { data } = useQuery({ queryKey: ["admin-reviews"], queryFn: () => api.get("/admin/reviews").then((r) => r.data) });
  const list = Array.isArray(data) ? data : [];
  const prods = Array.isArray(products) ? products : [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-reviews"] });
  return (
    <div data-testid="admin-reviews">
      <PendingReviews />
      <BeforeAfterSubmissions />
      <p className="mb-6 max-w-3xl text-sm text-[var(--parchment)]/85">Quand une cliente répond à l'email « Votre avis » (envoyé 21 jours après l'expédition), copiez ici son avis tel quel. Il s'affiche aussitôt sur la fiche du produit. Ne publiez un avis qu'avec l'accord écrit de la cliente.</p>
      <NewReview products={prods} onSaved={refresh} />
      {list.length === 0 ? <p data-testid="admin-reviews-empty" className="mt-8 text-sm text-[var(--parchment)]/80">Aucun avis pour l'instant.</p> : (
        <ul className="mt-8">{list.map((r) => <ReviewRow key={r.id} r={r} productName={prods.find((p) => p.slug === r.product_slug)?.name || r.product_slug} onChanged={refresh} />)}</ul>
      )}
    </div>
  );
}
